import React, { memo, useEffect, useState } from "react";
import humanizeDuration from "humanize-duration";
import { FaUserTie } from "react-icons/fa";

const stateManager = require("../../utils/state/stateManager");
const stringUtil = require("../../utils/string/stringUtil");
const formatter = require("../../utils/formatter/formatter");
const dateUtil = require("../../utils/dateUtil/dateUtil");
const saleCalculator = require("../../utils/domain/sales/saleCalculator");
const constants = require("../../utils/domain/constants");
const saleCommonUtility = require("../../utils/domain/sales/saleCommonUtility");
const translator = require("../../utils/translator/translator");
const floorLayoutUtil = require("../../utils/domain/floorLayoutUtil");

const Stencil = (props) => {
     const { width, top, left, tableNumber } = props.stencil;
     const [openSale, setOpenSale] = useState(null);
     const [mergedSale, setMergedSale] = useState(null);

     useEffect(() => {
          setOpenSale(floorLayoutUtil.getTableOpenSale(tableNumber));
          setMergedSale(floorLayoutUtil.getMergedTableSale(tableNumber));
     }, [tableNumber]);

     let backgroundColor = "var(--bs-dark)";
     let color = "white";
     let opacity = 1;
     let border = "6px solid var(--bs-dark)";
     let boxShadow = "0 0 10px 10px var(--colors-primary20)";
     let onClick = () => {
          if (props.onStencilClick != null) props.onStencilClick(props.stencil, openSale, mergedSale);
     };

     if (props.isStencilEnabled != null && !props.isStencilEnabled(props.stencil, openSale, mergedSale)) {
          opacity = 0.6;
          onClick = null;
     }

     if (props.isStencilSelected != null) {
          let selected = props.isStencilSelected(props.stencil, openSale, mergedSale);
          if (selected === "primary") {
               boxShadow = "#0d55ff40 0px 0px 0px 10px, #0d55ff40 0px 0px 0px 20px";
          } else if (selected === "secondary") {
               boxShadow = "#0dd2ff40 0px 0px 0px 10px, #0dd2ff40 0px 0px 0px 20px";
          }
     }

     const isSaleOpen = openSale != null;
     const isMergedWithSaleOpen = mergedSale != null;

     const getOpenSaleTime = () => {
          const differenceInMilliseconds = dateUtil.differenceInMilliseconds(dateUtil.getNow(), openSale.dateTime);
          const formattedDuration = humanizeDuration(differenceInMilliseconds, { units: ["h", "m"], round: true });
          const formattedDurationShort = formattedDuration.replace("hours", "h").replace("minutes", "m");
          return formattedDurationShort;
     };

     const tablePanel = (
          <div
               style={{
                    background: backgroundColor,
                    color: color,
                    overflow: "hidden",
                    paddingTop: "1.8rem",
               }}
          >
               {isSaleOpen && (
                    <div
                         className="text-center"
                         style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              whiteSpace: "nowrap",
                              backgroundColor: "var(--bs-green)",
                              fontSize: "0.9em",
                              padding: "2px 0px",
                         }}
                    >
                         Engaged
                    </div>
               )}
               {isMergedWithSaleOpen && (
                    <div
                         className="text-center"
                         style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              whiteSpace: "nowrap",
                              backgroundColor: "var(--bs-green)",
                              fontSize: "0.9em",
                              padding: "2px 0px",
                         }}
                    >
                         {`${translator.getLabel("merged_to")} ${mergedSale.tableNumber}`}
                    </div>
               )}
               <div style={{ fontSize: "2em", fontWeight: "bold", textAlign: "center" }}>{tableNumber}</div>
               {isSaleOpen && (
                    <div>
                         <div className="d-flex justify-content-center align-content-center" style={{ fontSize: "0.8rem" }}>
                              <div className="d-flex flex-row align-items-center gap-1">
                                   <div>{translator.getLabel("sale")}</div>
                                   <div style={{ fontSize: "1rem", color: "var(--bs-warning)" }}>
                                        {formatter.convertToCurrencyFormat(saleCalculator.calculateTotalNetSalePrice(openSale))}
                                   </div>
                              </div>
                         </div>

                         <div className="d-flex justify-content-center align-content-center" style={{ fontSize: "0.8rem" }}>
                              <div className="d-flex flex-row align-items-center gap-1" style={{ padding: "0px 5px" }}>
                                   <GuestIcon size="1.2em" />
                                   <div style={{ fontSize: "1rem" }}>{openSale.noOfGuests}</div>
                              </div>
                              <div className="d-flex flex-row align-items-center gap-1" style={{ padding: "0px 5px" }}>
                                   <TimeIcon size="1.2em" />
                                   <div style={{ fontSize: "0.9rem", color: "var(--bs-cyan)" }}>{getOpenSaleTime()}</div>
                              </div>
                         </div>
                         <div className="d-flex justify-content-center align-content-center" style={{ fontSize: "0.8rem" }}>
                              <div className="d-flex flex-row align-items-center gap-1">
                                   <FaUserTie size="1em" />
                                   <div>{openSale.server}</div>
                              </div>
                         </div>
                    </div>
               )}
          </div>
     );

     const components = [];
     populateGeneralAttributes(components);

     return (
          <div
               className="user-select-none"
               style={{
                    width,
                    minWidth: "180px",
                    position: "absolute",
                    transform:`translate(${left}px, ${top}px)`,
                    fontSize: "1rem",
                    background: backgroundColor,
                    overflow: "hidden",
                    borderRadius: "var(--button-roundness)",
                    boxShadow: boxShadow,
                    opacity: opacity,
               }}
               onClick={onClick}
          >
               <div>
                    {tablePanel}
                    <div
                         style={{
                              backgroundColor: "var(--colors-primary)",
                              border: border,
                              borderRadius: "calc(var(--button-roundness) * 2)",
                              overflow: "hidden",
                         }}
                    >
                         {components}
                    </div>
               </div>
          </div>
     );

     function populateGeneralAttributes(components) {
          let earnings = getTotalEarning();

          components.push(
               <div
                    className="d-flex justify-content-between"
                    style={{
                         color: "white",
                         borderBottom: "1px solid #00000040",
                         padding: "3px",
                         paddingLeft: "5px",
                         paddingRight: "5px",
                    }}
               >
                    <div style={{ fontSize: "0.9em" }}>{translator.getLabel("covers")}</div>
                    <div style={{ fontSize: "0.9em" }}>{props.stencil.noOfSeating}</div>
               </div>
          );

          components.push(
               <div
                    className="d-flex justify-content-between"
                    style={{
                         color: "white",
                         borderBottom: "1px solid #00000040",
                         padding: "3px",
                         paddingLeft: "5px",
                         paddingRight: "5px",
                    }}
               >
                    <div style={{ fontSize: "0.9em" }}>{translator.getLabel("total_guests")}</div>
                    <div style={{ fontSize: "0.9em" }}>{earnings.totalCovers}</div>
               </div>
          );

          components.push(
               <div
                    className="d-flex justify-content-between"
                    style={{
                         color: "white",
                         borderBottom: "1px solid #00000040",
                         padding: "3px",
                         paddingLeft: "5px",
                         paddingRight: "5px",
                    }}
               >
                    <div style={{ fontSize: "0.9em" }}>{translator.getLabel("total_Sale")}</div>
                    <div style={{ fontSize: "0.9em" }}>{formatter.convertToCurrencyFormat(earnings.totalSale)}</div>
               </div>
          );
     }

     function getTotalEarning() {
          let sales = stateManager.getStore().getState().todaySales;
          let tableSales = sales.filter(
               (sale) =>
                    !saleCommonUtility.isCancelled(sale) &&
                    sale.saleType !== constants.SaleTypeHold &&
                    stringUtil.areStringSame(sale.tableNumber, tableNumber) &&
                    saleCalculator.isSaleFullyPaid(sale)
          );
          let totalSale = 0;
          let totalCovers = 0;

          tableSales.forEach((tableSale) => {
               totalCovers += tableSale.noOfGuests;
               totalSale += saleCalculator.calculateTotalNetSalePrice(tableSale);
          });

          return { totalSale, totalCovers };
     }
};

export default Stencil;

const GuestIcon = memo(({ size }) => {
     return (
          <svg width={size} height={size} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
               <path
                    fillRule="evenodd"
                    d="M8.25 6.75a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zM15.75 9.75a3 3 0 116 0 3 3 0 01-6 0zM2.25 9.75a3 3 0 116 0 3 3 0 01-6 0zM6.31 15.117A6.745 6.745 0 0112 12a6.745 6.745 0 016.709 7.498.75.75 0 01-.372.568A12.696 12.696 0 0112 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 01-.372-.568 6.787 6.787 0 011.019-4.38z"
                    clipRule="evenodd"
               />
               <path d="M5.082 14.254a8.287 8.287 0 00-1.308 5.135 9.687 9.687 0 01-1.764-.44l-.115-.04a.563.563 0 01-.373-.487l-.01-.121a3.75 3.75 0 013.57-4.047zM20.226 19.389a8.287 8.287 0 00-1.308-5.135 3.75 3.75 0 013.57 4.047l-.01.121a.563.563 0 01-.373.486l-.115.04c-.567.2-1.156.349-1.764.441z" />
          </svg>
     );
});

const TimeIcon = memo(({ size }) => {
     return (
          <svg width={size} height={size} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
               <path
                    fillRule="evenodd"
                    d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z"
                    clipRule="evenodd"
               />
          </svg>
     );
});
